import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Award, Settings2, RefreshCw } from "lucide-react";

const GRADE = { A: "bg-green-100 text-green-700", B: "bg-blue-100 text-blue-700", C: "bg-amber-100 text-amber-800", D: "bg-red-100 text-red-700" };

export default function Scorecards() {
  const [cards, setCards] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [caps, setCaps] = useState([]);
  const [detail, setDetail] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/srm/scorecards").then((r) => setCards(r.data || [])).catch((e) => toast.error(errDetail(e))); }, []);
  useEffect(() => {
    api.get("/srm/scorecard/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/srm/my-caps").then((r) => setCaps(r.data.caps || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const canEdit = caps.includes("scorecard_edit") || caps.includes("srm_admin");
  const generate = async () => { setBusy(true); try { const r = await api.post("/srm/scorecard/generate", {}); toast.success(`${r.data.count} Scorecards für ${r.data.period}`); load(); } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); } };

  return (
    <div className="p-6 max-w-[1300px] mx-auto" data-testid="scorecard-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><Award className="h-5 w-5 text-[#F5A623]" /></div>
          <div><h1 className="text-2xl font-bold text-[#1B2A4A]">Lieferanten-Scorecards</h1><p className="text-sm text-gray-500">Quartalsweise · kategorie-gewichtet · A/B/C/D · Peer-Perzentil</p></div>
        </div>
        {canEdit && <div className="flex gap-2">
          <Button variant="outline" data-testid="scorecard-config-btn" onClick={() => setCfgOpen(true)}><Settings2 className="h-4 w-4 mr-1" /> Gewichtung</Button>
          <Button data-testid="scorecard-generate-btn" disabled={busy} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={generate}><RefreshCw className="h-4 w-4 mr-1" /> Neu berechnen</Button>
        </div>}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Lieferant</TableHead><TableHead>Periode</TableHead><TableHead className="text-right">Score</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Peer-Perzentil</TableHead><TableHead>Anpassung</TableHead></TableRow></TableHeader>
          <TableBody>
            {cards.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">Noch keine Scorecards — „Neu berechnen" ausführen</TableCell></TableRow>}
            {cards.map((c) => (
              <TableRow key={c.id} className="cursor-pointer hover:bg-gray-50" data-testid={`scorecard-row-${c.supplier_id}`} onClick={() => setDetail(c.id)}>
                <TableCell className="font-medium text-[#1B2A4A]">{c.supplier_name}</TableCell><TableCell>{c.period}</TableCell>
                <TableCell className="text-right font-semibold">{c.weighted_score}</TableCell>
                <TableCell><Badge className={GRADE[c.grade]}>{c.grade}</Badge></TableCell>
                <TableCell className="text-right text-gray-600">{c.peer_percentile}%</TableCell>
                <TableCell>{c.manual_adjustment ? <Badge className="bg-purple-100 text-purple-700">manuell {c.manual_adjustment.delta > 0 ? "+" : ""}{c.manual_adjustment.delta}</Badge> : <span className="text-gray-300">–</span>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {detail && <ScorecardDetail id={detail} cfg={cfg} canEdit={canEdit} onClose={(ch) => { setDetail(null); if (ch) load(); }} />}
      {cfgOpen && cfg && <ConfigDialog cfg={cfg} onClose={(ch) => { setCfgOpen(false); if (ch) { api.get("/srm/scorecard/config").then((r) => setCfg(r.data)); } }} />}
    </div>
  );
}

function ScorecardDetail({ id, cfg, canEdit, onClose }) {
  const [c, setC] = useState(null);
  const [delta, setDelta] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/srm/scorecards/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errDetail(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  const adjust = async () => {
    if (!comment) { toast.error("Pflichtkommentar erforderlich"); return; }
    setBusy(true);
    try { await api.post(`/srm/scorecards/${id}/adjust`, { delta: Number(delta), comment }); toast.success("Angepasst"); onClose(true); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  if (!c) return null;
  const labels = Object.fromEntries((cfg?.metrics || []).map((m) => [m.key, m.label]));
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="scorecard-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2">{c.supplier_name} <Badge className={GRADE[c.grade]}>{c.grade}</Badge></DialogTitle>
          <DialogDescription>{c.period} · Gesamtscore <b>{c.weighted_score}</b> · Peer-Perzentil {c.peer_percentile}%</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {Object.entries(c.scores).map(([k, v]) => (
            <div key={k} data-testid={`sc-metric-${k}`}><div className="flex justify-between text-sm mb-1"><span>{labels[k] || k} <span className="text-gray-400">({cfg?.weights?.[k]}%)</span></span><span className="font-medium">{v}</span></div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-[#1B2A4A]" style={{ width: `${v}%` }} /></div></div>
          ))}
          {c.manual_adjustment && <div className="rounded-lg bg-purple-50 border border-purple-200 p-2 text-sm text-purple-800">Manuell {c.manual_adjustment.delta > 0 ? "+" : ""}{c.manual_adjustment.delta} von {c.manual_adjustment.by}: {c.manual_adjustment.comment}</div>}
          {canEdit && (
            <div className="border-t pt-3 space-y-2">
              <Label>Manuelle Anpassung (Pflichtkommentar)</Label>
              <div className="flex gap-2"><Input className="w-24" type="number" data-testid="sc-adjust-delta" value={delta} onChange={(e) => setDelta(e.target.value)} />
                <Input className="flex-1" placeholder="Begründung…" data-testid="sc-adjust-comment" value={comment} onChange={(e) => setComment(e.target.value)} /></div>
              <Button size="sm" data-testid="sc-adjust-btn" disabled={busy || !comment} onClick={adjust}>Anpassen</Button>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Schließen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigDialog({ cfg, onClose }) {
  const [w, setW] = useState({ ...cfg.weights });
  const [busy, setBusy] = useState(false);
  const total = Object.values(w).reduce((s, v) => s + Number(v || 0), 0);
  const save = async () => {
    setBusy(true);
    try { await api.put("/srm/scorecard/config", { weights: w, thresholds: cfg.thresholds }); toast.success("Gewichtung gespeichert"); onClose(true); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md" data-testid="scorecard-config-dialog">
        <DialogHeader><DialogTitle>Gewichtung (Summe muss 100 % sein)</DialogTitle><DialogDescription>Kategorie-Gewichte für den Gesamtscore.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {cfg.metrics.map((m) => (
            <div key={m.key} className="flex items-center justify-between gap-2"><Label className="flex-1">{m.label}</Label>
              <Input className="w-24" type="number" data-testid={`weight-${m.key}`} value={w[m.key] ?? 0} onChange={(e) => setW((s) => ({ ...s, [m.key]: e.target.value }))} /></div>
          ))}
          <div className={`text-right font-semibold ${total === 100 ? "text-green-600" : "text-red-600"}`} data-testid="weight-total">Summe: {total}%</div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Abbrechen</Button>
          <Button data-testid="weight-save-btn" disabled={busy || total !== 100} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={save}>Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
